import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_EVALUATIONCRITERIA } from "../../../../utils/constants";
import { EvaluationCriteriaInterface } from "../../../../models/ProcurementModule/EvaluationCriteria";
import EvaluationCriteriaService from "../../../../services/procurementService/EvaluationCriteriaService";

interface UpdateEvaluationCriteriaContext {
  previousEvaluationCriteria: EvaluationCriteriaInterface[];
}

const useUpdateEvaluationCriteria = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    EvaluationCriteriaInterface,
    Error,
    EvaluationCriteriaInterface,
    UpdateEvaluationCriteriaContext
  >({
    mutationFn: async (vendor: EvaluationCriteriaInterface) => {
      // Assuming the ID is stored in the vendor object or can be derived
      const { id, ...data } = vendor;
      return EvaluationCriteriaService.Update(id || "", data);
    },

    onMutate: async (updatedData: EvaluationCriteriaInterface) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_EVALUATIONCRITERIA, page, pageSize],
      });

      const previousEvaluationCriteria =
        queryClient.getQueryData<EvaluationCriteriaInterface[]>([
          CACHE_KEY_EVALUATIONCRITERIA,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<EvaluationCriteriaInterface[]>(
        [CACHE_KEY_EVALUATIONCRITERIA, page, pageSize],
        (data = []) =>
          data.map((item) => (item.id === updatedData.id ? updatedData : item))
      );

      return { previousEvaluationCriteria };
    },

    onSuccess: (savedData) => {
      queryClient.setQueryData<EvaluationCriteriaInterface[]>(
        [CACHE_KEY_EVALUATIONCRITERIA, page, pageSize],
        (data) =>
          data?.map((item) => (item.id === savedData.id ? savedData : item))
      );
    },

    onError: (error, updatedData, context) => {
      if (!context) return;

      queryClient.setQueryData<EvaluationCriteriaInterface[]>(
        [CACHE_KEY_EVALUATIONCRITERIA, page, pageSize],
        context.previousEvaluationCriteria
      );
    },
  });
};

export default useUpdateEvaluationCriteria;
